/**
 * AuthDemo - Demo component showing all auth page layouts
 * This is for the Design system page to preview auth components
 */

import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/Card'
import { Ghost, Wand2, KeyRound } from 'lucide-react'
import { GoogleIcon, MicrosoftIcon } from '@/components/icons'

type AuthPage = 'login' | 'register' | 'magic-link' | 'forgot-password' | 'reset-password'

export function AuthDemo() {
  const [activePage, setActivePage] = useState<AuthPage>('login')

  return (
    <div className="space-y-6">
      {/* Page Selector */}
      <div className="flex flex-wrap gap-2 justify-center">
        <Button
          variant={activePage === 'login' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActivePage('login')}
          className="cursor-pointer"
        >
          Login
        </Button>
        <Button
          variant={activePage === 'register' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActivePage('register')}
          className="cursor-pointer"
        >
          Register
        </Button>
        <Button
          variant={activePage === 'magic-link' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActivePage('magic-link')}
          className="cursor-pointer"
        >
          Magic Link
        </Button>
        <Button
          variant={activePage === 'forgot-password' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActivePage('forgot-password')}
          className="cursor-pointer"
        >
          Forgot Password
        </Button>
        <Button
          variant={activePage === 'reset-password' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActivePage('reset-password')}
          className="cursor-pointer"
        >
          Reset Password
        </Button>
      </div>

      {/* Page Preview */}
      <div className="border rounded-lg bg-background min-h-[700px] flex items-center justify-center p-4">
        {activePage === 'login' && <LoginPreview />}
        {activePage === 'register' && <RegisterPreview />}
        {activePage === 'magic-link' && <MagicLinkPreview />}
        {activePage === 'forgot-password' && <ForgotPasswordPreview />}
        {activePage === 'reset-password' && <ResetPasswordPreview />}
      </div>
    </div>
  )
}

function Divider({ text = 'Or' }: { text?: string }) {
  return (
    <div className="relative">
      <div className="absolute inset-0 flex items-center">
        <span className="w-full border-t" />
      </div>
      <div className="relative flex justify-center text-xs uppercase">
        <span className="bg-card px-2 text-muted-foreground">{text}</span>
      </div>
    </div>
  )
}

function LoginPreview() {
  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <Ghost className="size-12 text-primary mx-auto mb-2" />
        <CardTitle>Welcome back</CardTitle>
        <CardDescription>Sign in to your Banshee account</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Email/Password Form */}
        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <div className="space-y-2">
            <Label htmlFor="login-email">Email</Label>
            <Input
              id="login-email"
              type="email"
              placeholder="ghost@example.com"
              defaultValue="ghost@banshee.chat"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="login-password">Password</Label>
              <span className="text-xs text-muted-foreground hover:text-foreground cursor-pointer">
                Forgot password?
              </span>
            </div>
            <Input
              id="login-password"
              type="password"
              defaultValue="secretpassword"
            />
          </div>

          <Button type="submit" className="w-full cursor-pointer">
            Sign in
          </Button>
        </form>

        <Divider />

        {/* Alternative Sign-in Options */}
        <div className="space-y-3">
          <Button variant="outline" className="w-full cursor-pointer">
            <GoogleIcon className="size-5" />
            Continue with Google
          </Button>
          <Button variant="outline" className="w-full cursor-pointer">
            <MicrosoftIcon className="size-5" />
            Continue with Microsoft
          </Button>
          <Button variant="outline" className="w-full cursor-pointer">
            <Wand2 className="size-4" />
            Sign in with magic link
          </Button>
        </div>
      </CardContent>

      <CardFooter className="justify-center">
        <p className="text-sm text-muted-foreground">
          Don't have an account?{' '}
          <span className="text-primary hover:underline cursor-pointer">Sign up</span>
        </p>
      </CardFooter>
    </Card>
  )
}

function RegisterPreview() {
  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <Ghost className="size-12 text-primary mx-auto mb-2" />
        <CardTitle>Create your account</CardTitle>
        <CardDescription>Start chatting with your customers</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Email/Password Form */}
        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="reg-first">First name</Label>
              <Input id="reg-first" defaultValue="Ghost" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-last">Last name</Label>
              <Input id="reg-last" defaultValue="Hunter" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reg-email">Email</Label>
            <Input
              id="reg-email"
              type="email"
              placeholder="ghost@example.com"
              defaultValue="ghost@banshee.chat"
            />
            <p className="text-sm text-destructive">Email has already been taken</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reg-password">Password</Label>
            <Input id="reg-password" type="password" defaultValue="secretpass" />
            <p className="text-xs text-muted-foreground">Minimum 12 characters</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reg-confirm">Confirm password</Label>
            <Input id="reg-confirm" type="password" defaultValue="secretpass" />
          </div>

          <Button type="submit" className="w-full cursor-pointer">
            Create account
          </Button>
        </form>

        <Divider />

        {/* Alternative Sign-up Options */}
        <div className="space-y-3">
          <Button variant="outline" className="w-full cursor-pointer">
            <GoogleIcon className="size-5" />
            Continue with Google
          </Button>
          <Button variant="outline" className="w-full cursor-pointer">
            <MicrosoftIcon className="size-5" />
            Continue with Microsoft
          </Button>
          <Button variant="outline" className="w-full cursor-pointer">
            <Wand2 className="size-4" />
            Sign up with magic link
          </Button>
        </div>
      </CardContent>

      <CardFooter className="justify-center">
        <p className="text-sm text-muted-foreground">
          Already have an account?{' '}
          <span className="text-primary hover:underline cursor-pointer">Sign in</span>
        </p>
      </CardFooter>
    </Card>
  )
}

function MagicLinkPreview() {
  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <Wand2 className="size-12 text-primary mx-auto mb-2" />
        <CardTitle>Sign in with magic link</CardTitle>
        <CardDescription>We'll email you a link to sign in instantly</CardDescription>
      </CardHeader>

      <CardContent>
        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <div className="space-y-2">
            <Label htmlFor="magic-email">Email</Label>
            <Input
              id="magic-email"
              type="email"
              placeholder="ghost@example.com"
            />
          </div>

          <Button type="submit" className="w-full cursor-pointer">
            Send magic link
          </Button>
        </form>
      </CardContent>

      <CardFooter className="justify-center">
        <span className="text-sm text-muted-foreground hover:text-foreground cursor-pointer">
          Back to login
        </span>
      </CardFooter>
    </Card>
  )
}

function ForgotPasswordPreview() {
  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <KeyRound className="size-12 text-primary mx-auto mb-2" />
        <CardTitle>Reset your password</CardTitle>
        <CardDescription>Enter your email and we'll send reset instructions</CardDescription>
      </CardHeader>

      <CardContent>
        <div className="mb-4 p-3 rounded-lg bg-primary/10 text-sm text-primary">
          If that email exists, you'll receive reset instructions.
        </div>

        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <div className="space-y-2">
            <Label htmlFor="forgot-email">Email</Label>
            <Input
              id="forgot-email"
              type="email"
              placeholder="ghost@example.com"
            />
          </div>

          <Button type="submit" className="w-full cursor-pointer">
            Send reset instructions
          </Button>
        </form>
      </CardContent>

      <CardFooter className="justify-center">
        <span className="text-sm text-muted-foreground hover:text-foreground cursor-pointer">
          Back to login
        </span>
      </CardFooter>
    </Card>
  )
}

function ResetPasswordPreview() {
  return (
    <Card className="w-full max-w-md">
      <CardHeader className="text-center">
        <KeyRound className="size-12 text-primary mx-auto mb-2" />
        <CardTitle>Set new password</CardTitle>
        <CardDescription>Enter your new password below</CardDescription>
      </CardHeader>

      <CardContent>
        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <div className="space-y-2">
            <Label htmlFor="reset-password">New password</Label>
            <Input id="reset-password" type="password" />
            <p className="text-xs text-muted-foreground">Minimum 12 characters</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reset-confirm">Confirm new password</Label>
            <Input id="reset-confirm" type="password" />
          </div>

          <Button type="submit" className="w-full cursor-pointer">
            Update password
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

export default AuthDemo
